import { GoogleLogin } from "@react-oauth/google";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import RegistrationSuccessOverlay from "../components/RegistrationSuccessOverlay.jsx";
import { useAuth } from "../hooks/useAuth.js";
import { clearPendingAttempts, getPendingAttempts } from "../utils/localScoreCache.js";

const RegisterPage = () => {
  const { register, googleAuth } = useAuth();
  const navigate = useNavigate();

  const pendingAttempts = useMemo(() => getPendingAttempts(), []);
  const pendingPoints = pendingAttempts.reduce((sum, a) => sum + (a.points_would_earn || 0), 0);

  const [fields, setFields] = useState({
    username: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successPoints, setSuccessPoints] = useState(null);

  const handleGoogleSuccess = async ({ credential }) => {
    try {
      const data = await googleAuth(credential, pendingAttempts);
      if (data.status === "onboarding") {
        sessionStorage.setItem("google_onboarding", JSON.stringify(data));
        navigate("/register/google", { replace: true });
      } else {
        // Server redeemed pending attempts — safe to clear the local cache
        clearPendingAttempts();
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChange = (e) => {
    setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        username: fields.username,
        email: fields.email,
        password: fields.password,
        ...(pendingAttempts.length > 0 && {
          pending_attempts: pendingAttempts.map(({ quiz_slug, attempt_token }) => ({
            quiz_slug,
            attempt_token,
          })),
        }),
      };
      const data = await register(payload);
      clearPendingAttempts();
      setSuccessPoints(data?.retroactive_points ?? 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (successPoints !== null) {
    return (
      <RegistrationSuccessOverlay
        points={successPoints}
        onContinue={() => navigate("/", { replace: true })}
      />
    );
  }

  return (
    <div className="mx-auto max-w-sm pt-8">
      <Helmet>
        <title>Create Account — QuizFreak</title>
        <meta name="description" content="Join QuizFreak to save your scores, earn points, and climb the leaderboard." />
        <link rel="canonical" href="https://quizfreak.com/v2/register/" />
      </Helmet>
      <h1 className="text-3xl font-semibold text-white">Create account</h1>
      <p className="mt-1 text-sm text-white/60">
        Already have one?{" "}
        <Link to="/login" className="text-brand-glow hover:underline">
          Sign in
        </Link>
      </p>

      {pendingAttempts.length > 0 && (
        <div className="mt-6 rounded-2xl border border-brand-glow/30 bg-brand-glow/10 px-4 py-3">
          <p className="text-sm font-semibold text-white">
            {pendingAttempts.length === 1
              ? "You have 1 unsaved quiz score"
              : `You have ${pendingAttempts.length} unsaved quiz scores`}
          </p>
          <p className="mt-0.5 text-sm text-white/70">
            Create an account to claim <span className="font-semibold text-brand-glow">{pendingPoints} pts</span>
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <div>
          <label className="block text-sm font-medium text-white/80" htmlFor="username">
            Username <span className="text-red-400">*</span>
          </label>
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            required
            value={fields.username}
            onChange={handleChange}
            className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/30 focus:border-brand-glow/60 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white/80" htmlFor="email">
            Email <span className="text-red-400">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={fields.email}
            onChange={handleChange}
            className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/30 focus:border-brand-glow/60 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white/80" htmlFor="password">
            Password <span className="text-red-400">*</span>
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            value={fields.password}
            onChange={handleChange}
            className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/30 focus:border-brand-glow/60 focus:outline-none"
          />
        </div>

        {error &&<p className="text-sm text-red-300">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-brand-glow px-6 py-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          {submitting ? "Creating account…" : "Create account"}
        </button>
      </form>

      <div className="mt-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-white/10" />
        <span className="text-xs text-white/40">or</span>
        <div className="h-px flex-1 bg-white/10" />
      </div>

      <div className="mt-4 flex justify-center">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => setError("Google sign-in failed. Please try again.")}
          theme="filled_black"
          shape="pill"
          text="signup_with"
        />
      </div>
    </div>
  );
};

export default RegisterPage;
